'use client';
import '@css/insurance-form/insurance-form.scss';
import IF_Step1 from "./steps/step1/step-1";
import IF_Step2 from "./steps/step2/step-2";
import IF_Step3 from "./steps/step3/step-3";
import {useEffect, useRef, useState} from "react";
import IF_Step4 from "./steps/step4/step-4";
import DriverInfo from "@classes/driver-info";
import AccidentInfo from "@classes/accident-info";
import {API_CAR_INSURANCE_QUOTE_NEW, API_CAR_INSURANCE_QUOTE_UPDATE} from "@constants/next-api-endpoints";
import IF_Step0 from "./steps/step0/step-0";
import FormSubmissionProcessingLoader from "@components/common/ui-elements/loader/form-submission-processing-loader";
import IF_Step5 from "./steps/step5/step-5";
import ConvictionInfo from "@classes/conviction-info";
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {checkAndAddCssClassToRefContainerChildren} from "@utils/dom";
import {insuranceFormOnSubmit} from "@functions/insurance-form-on-submit";
import UseTestData from "@components/common/use-test-data";
import {CarInsuranceQuoteTestData as TestData} from "@app/test-form-data/car-insurance-quote";
import {isDetailsMode, isEditableMode, isEditMode, isNewMode, useFormMode} from "@contexts/FormModeContext";
import Link from "next/link";
import {DASH_LINK_CAR_INSURANCE_QUOTE_EDIT, DASH_LINK_CAR_INSURANCE_QUOTE_LIST} from "@constants/dash-links";
import SubmissionDetailsHeadingBlock
   from "@components/common/sub-components/insurance-form-parts/submission-details-heading-block";
import QuickStepList from "@components/common/sub-components/edit-insurance/quick-step-list";
import QuoteFormSeoIntro from "@components/seo/quote-form-seo-intro";
import {
   BACKEND_ACCIDENT_MANAGEMENT_CLAIM_NEW,
   BACKEND_CAR_INSURANCE_QUOTE_NEW, BACKEND_CAR_INSURANCE_QUOTE_UPDATE
} from "../../../../constants/laravel-backend-endpoints";

const EDIT_URL = DASH_LINK_CAR_INSURANCE_QUOTE_EDIT;
const LIST_URL = DASH_LINK_CAR_INSURANCE_QUOTE_LIST;

export default function CarInsuranceForm({
   testData = false,
   initialFormData = {},
   firstStep = 0,
}) {
   
   const {mode} = useFormMode();
   
   const FIRST_STEP = firstStep;
   const LAST_STEP = 5;
   
   const STEP_LIST = [
      {
         step: 0,
         label: 'Contact Details',
      },
      {
         step: 1,
         label: 'Personal Information',
      },
      {
         step: 2,
         label: 'Driving History',
      },
      {
         step: 3,
         label: 'Car Information',
      },
      {
         step: 4,
         label: 'Claims and Convictions',
      },
      {
         step: 5,
         label: 'Additional Driver Details',
      }
   ];
   
   const {executeRecaptcha} = useGoogleReCaptcha();
   
   const stepContainerRef = useRef(null);
   
   const [useTestData, setUseTestData] = useState(testData);
   
   const [isSubmissionProcessing, setIsSubmissionProcessing] = useState(false);
   const [submissionSuccessMsg, setSubmissionSuccessMsg] = useState('');
   const [submissionErrorMsg, setSubmissionErrorMsg] = useState('');
   const [submissionProcessingMsg, setSubmissionProcessingMsg] = useState('');
   
   const formRef = useRef(null);
   const cifContainerRef = useRef(null);
   
   const [serverValidationErrors, setServerValidationErrors] = useState([]);
   
   const defaultData = {
      ...{
         
         /**
          * Step 0 data ...
          */
         
         discoveryType: '',
         referrersName: '',
         contactOptions: [],
         discountCode: '',
         
         /**
          * Step 1 : Data ....
          */
         
         
         
         title: '',
         firstName: '',
         lastName: '',
         birthDate: '',
         
         houseNumber: '',
         addressLine1: '',
         addressLine2: '',
         
         postCode: '',
         maritalStatus: '',
         employmentStatus: '',
         
         isHomeOwner: '',
         hasChildrenUnder16: '',
         numChildrenUnder16: '',
         
         contactEmail: '',
         contactPhone: '',
         
         mainOccupation: '',
         occupationIndustry: '',
         
         /**
          * Step 2 Data ...
          */
         
         drivingLicenseType: '',
         provideDrivingLicenseNumber: '',
         britishDrivingLicenseNumber: '',
         drivingLicenseDuration: '',
         drivingLicenseDate: '',
         dvlaMedicalConditions: '',
         additionalDrivingQualifications: '',
         additionalDrivingQualificationDate: '',
         isBornInUk: '',
         lastDateBecameUKResident: '',
         hasNonMotoringCriminalConvictions: '',
         nonMotoringCriminalConvictionsDetails: '',
         otherCarUsage: '',
         
         hasAccidentClaimsInLast5Years: '',
         motorAccidentsList: [],
         motorConvictionsList: [],
         hasMotoringConvictionsInLast5Years: '',
         
         /**
          * Step 3: Car info ...
          */
         
         isCarRegistrationNumKnown: '',
         
         carRegistrationNumber: '',
         carMakeAndModel: '',
         carEstimatedValue: '',
         
         manufacturerName: '',
         manufacturerModel: '',
         carRegistrationYear: '',
         carAlarmsOrImmobiliser: '',
         carSeatNumber: '',
         carDoorNumber: '',
         carEngineSize: '',
         carTransmissionType: '',
         
         hasModifications: '',
         modificationType: '',
         modificationDetails: '',
         
         wasCarImported: '',
         
         carPurchased: '',
         carPurchaseDate: '',
         
         
         registeredKeeper: '',
         legalOwner: '',
         isTheCarOnFinance: '',
         carImpoundedWithPolice: '',
         
         /**
          * Car usage ...
          */
         
         carParkingAtDay: '',
         carParkingAtNight: '',
         isCarKeptOnSameAddress: '',
         carKeeperAddress: '',
         
         wasInsuranceDeclinedBefore: '',
         insurerName: '',
         
         carUsageType: '',
         carUsageTypes: [],
         estimatedAnnualMileage: '',
         
         insuranceCoverageType: '',
         noClaimsDiscount: '',
         
         voluntaryExcessAmounts: '',
         
         insurancePaymentType: '',
         
         currentOrPreviousInsurerName: '',
         renewalPriceOfPreviousInsurer: '',
         insuranceCoverageStartDate: '',
         
         /**
          * Step 4 ...
          */
         
         shouldAddAdditionalDrivers: '',
         
         additionalDrivers: [],
         
         addAdditionalComments: '',
         
         termsAgreement: '',
         
      },
      ...initialFormData,
   };
   
   const [formData, setFormData] = useState(defaultData);
   
   const [currentStep, setCurrentStep] = useState(FIRST_STEP);
   
   const defaultFadeInClasses = {
      0: '',
      1: '',
      2: '',
      3: '',
      4: '',
      5: '',
   }
   
   const [stepClasses, setStepClasses] = useState(defaultFadeInClasses);
   
   useEffect(() => {
      if (useTestData) {
         setFormData(f => {
            return {
               ...f,
               ...TestData,
            }
         })
      } else {
         setFormData(defaultData);
      }
   }, [useTestData]);
   
   useEffect(() => {
      
      checkAndAddCssClassToRefContainerChildren({
         refContainer: cifContainerRef,
         selector: '.step-container',
         className: 'fade-in',
      });
      
   }, [currentStep]);
   
   const isArrayTypeControl = (name) => {
      return [
         'contactOptions[]',
         'carUsageTypes[]',
      ].includes(name);
   }
   
   const arrayTypeControlMapping = () => {
      return {
         'contactOptions[]': 'contactOptions',
         'carUsageTypes[]': 'carUsageTypes',
      };
   }
   
   const eventHandlers = {
      onControlChange: (event, callback) => {
         
         if (typeof event !== 'object') {
            return;
         }
         
         const {name, value, checked} = event.target;
         
         if (isArrayTypeControl(name)) {
            
            let propName = arrayTypeControlMapping()[name];
            
            if (propName) {
               
               
               let existingOptions = formData[propName];
               
               if (checked) {
                  
                  existingOptions.push(value);
                  
               } else {
                  existingOptions = existingOptions.filter((item) => {
                     return item !== value;
                  });
               }
               
               setFormData({
                  ...formData,
                  [propName]: existingOptions
               });
               
            }
            
         } else {
            setFormData({
               ...formData,
               [name]: value
            });
         }
         
         
         if (typeof callback === 'function') {
            callback.apply(null);
         }
      }
   };
   
   // region [Additional Driver Related ...]
   
   const addAdditionalDriver = () => {
      setFormData({
         ...formData,
         
         additionalDrivers: [...formData.additionalDrivers, new DriverInfo({})]
         
      });
   }
   
   const handleAdditionalDiverPropChange = (index, prop, value) => {
      const updatedDrivers = formData.additionalDrivers?.map((driver, i) => {
         if (i === index) {
            return {...driver, [prop]: value};
         }
         return driver;
      });
      
      setFormData({
         ...formData,
         additionalDrivers: updatedDrivers
      });
   };
   
   const removeAdditionalDiverByIndex = (index) => {
      const updatedDrivers = formData.additionalDrivers.filter((driver, i) => {
         return i !== index;
      });
      
      setFormData({
         ...formData,
         additionalDrivers: updatedDrivers
      });
   };
   
   // endregion
   
   // region [Additional driver accident related]
   
   const handleAdditionalDriverAccidentPropChange = (driverIndex, accidentIndex, propName, propValue) => {
      
      const updatedDrivers = formData.additionalDrivers?.map((driver, i) => {
         if (i === driverIndex) {
            
            const updatedMotorAccidentsList = driver.motorAccidentsList?.map((accident, j) => {
               
               if (j === accidentIndex) {
                  
                  return {...accident, [propName]: propValue};
                  
               }
               
               return accident;
               
            });
            
            return {...driver, motorAccidentsList: updatedMotorAccidentsList};
         }
         return driver;
      });
      
      
      setFormData({
         ...formData,
         additionalDrivers: updatedDrivers
      });
      
   }
   
   const addAdditionalDriverAccident = (driverIndex) => {
      
      
      let updatedAdditionalDrivers = formData.additionalDrivers?.map((driver, index) => {
         
         if (index === driverIndex) {
            
            return {
               ...driver,
               motorAccidentsList: [...driver.motorAccidentsList, new AccidentInfo({})]
            };
            
         }
         
         return driver;
         
      });
      
      
      setFormData({
         ...formData,
         
         additionalDrivers: updatedAdditionalDrivers
         
      });
   }
   
   const removeAdditionalDriverAccident = (driverIndex, accidentIndex) => {
      
      let updatedAdditionalDrivers = formData.additionalDrivers?.map((driver, index) => {
         
         if (index === driverIndex) {
            
            return {
               ...driver,
               motorAccidentsList: driver.motorAccidentsList.filter((accident, index) => {
                  return index !== accidentIndex;
               })
            };
            
         }
         
         return driver;
         
      })
      
      
      setFormData({
         ...formData,
         
         additionalDrivers: updatedAdditionalDrivers
         
      });
   }
   
   // endregion
   
   // region [Additional conviction accident related]
   
   const handleAdditionalDriverConvictionPropChange = (driverIndex, convictionIndex, propName, propValue) => {
      
      const updatedDrivers = formData.additionalDrivers?.map((driver, i) => {
         if (i === driverIndex) {
            
            const updatedMotoringConvictionsList = driver.motoringConvictionsList?.map((conviction, j) => {
               
               if (j === convictionIndex) {
                  
                  return {...conviction, [propName]: propValue};
                  
               }
               
               return conviction;
               
            });
            
            return {...driver, motoringConvictionsList: updatedMotoringConvictionsList};
         }
         return driver;
      });
      
      setFormData({
         ...formData,
         additionalDrivers: updatedDrivers
      });
      
   }
   
   const addAdditionalDriverConviction = (driverIndex) => {
      
      
      let updatedAdditionalDrivers = formData.additionalDrivers?.map((driver, index) => {
         
         if (index === driverIndex) {
            
            return {
               ...driver,
               motoringConvictionsList: [...driver.motoringConvictionsList, new ConvictionInfo({})]
            };
            
         }
         
         return driver;
         
      });
      
      
      setFormData({
         ...formData,
         
         additionalDrivers: updatedAdditionalDrivers
         
      });
   }
   
   const removeAdditionalDriverConviction = (driverIndex, convictionIndex) => {
      
      let updatedAdditionalDrivers = formData.additionalDrivers?.map((driver, index) => {
         
         if (index === driverIndex) {
            
            return {
               ...driver,
               motoringConvictionsList: driver.motoringConvictionsList.filter((conviction, index) => {
                  return index !== convictionIndex;
               })
            };
            
         }
         
         return driver;
         
      })
      
      
      setFormData({
         ...formData,
         
         additionalDrivers: updatedAdditionalDrivers
         
      });
   }
   
   // endregion
   
   // region [Policyholder accident related]
   
   const handlePolicyHolderAccidentPropChange = (accidentIndex, propName, propValue) => {
      
      let updatedMotorAccidentsList = formData.motorAccidentsList?.map((accident, index) => {
         
         if (index === accidentIndex) {
            
            return {
               ...accident,
               [propName]: propValue
            };
            
         }
         
         return accident;
         
      });
      
      
      setFormData({
         ...formData,
         motorAccidentsList: updatedMotorAccidentsList
      });
      
   }
   
   const addPolicyHolderAccident = () => {
      
      setFormData({
         ...formData,
         
         motorAccidentsList: [...formData.motorAccidentsList, new AccidentInfo({})]
         
      });
   }
   
   const removePolicyHolderAccident = (accidentIndex) => {
      
      
      let updatedMotorAccidentsList = formData.motorAccidentsList.filter((accident, index) => {
         return index !== accidentIndex;
      });
      
      setFormData({
         ...formData,
         
         motorAccidentsList: updatedMotorAccidentsList
         
      });
   }
   
   // endregion
   
   // region [Policyholder conviction related]
   
   const handlePolicyHolderConvictionPropChange = (convictionIndex, propName, propValue) => {
      
      let updatedMotorConvictionsList = formData.motorConvictionsList?.map((conviction, index) => {
         
         if (index === convictionIndex) {
            
            return {
               ...conviction,
               [propName]: propValue
            };
            
         }
         
         return conviction;
         
      });
      
      
      setFormData({
         ...formData,
         motorConvictionsList: updatedMotorConvictionsList
      });
      
   }
   
   const addPolicyHolderConviction = () => {
      
      setFormData({
         ...formData,
         
         motorConvictionsList: [...formData.motorConvictionsList, new ConvictionInfo({})]
         
      });
   }
   
   const removePolicyHolderConviction = (convictionIndex) => {
      
      
      let updatedMotorConvictionsList = formData.motorConvictionsList.filter((conviction, index) => {
         return index !== convictionIndex;
      });
      
      setFormData({
         ...formData,
         
         motorConvictionsList: updatedMotorConvictionsList
         
      });
   }
   
   // endregion
   
   const ServerValidationMessageList = () => {
      return (
         <div className="alert alert-danger m-3 p-3 text-left">
            <h3 className="mb-4">Please fix these issues and try again - </h3>
            
            <ol>
               {serverValidationErrors?.map((error, index) => {
                  return (
                     <li key={index}>{error}</li>
                  );
               })}
            </ol>
            
            
            <strong>Jump to - </strong>
            
            <ul>
               
               {STEP_LIST?.map((item, index) => {
                  return (
                     <li key={index}>
                        <a
                           href="#"
                           onClick={(e) => {
                              e.preventDefault();
                              backToForm({
                                 step: item.step
                              })
                           }}
                        >Step {item.step + 1}</a>
                        <span> - {item.label}</span>
                     </li>
                  );
               })}
            
            </ul>
         </div>
      )
   }
   
   const SubmissionErrorMessage = () => {
      return (
         <div className="alert alert-danger m-3 p-3 text-center">
            {submissionErrorMsg}
            <br />
            <br />
            <button type="button" className="btn btn-primary" onClick={() => {
               backToForm({step: currentStep})
            }}>
               <i className="fa fa-chevron-left" /> Back
            </button>
         
         </div>
      );
   }
   
   const SubmissionSuccessMessage = () => {
      return (
         <div className="alert alert-success p-3 m-3 text-center">
            
         <div
            dangerouslySetInnerHTML={{ __html: submissionSuccessMsg }}
         />
            
            <br />
         
            
            {isNewMode(mode) && (
               <>
                  <button
                     type="button"
                     className="btn btn-primary" onClick={(e) => {
                     setUseTestData(false);
                     backToForm({
                        step: FIRST_STEP,
                     })
                  }}
                  >
                     Submit another application
                  </button>
               </>
            )}
            
            {isEditMode(mode) && (
               <div className="d-flex justify-content-center align-items-center gap-3">
                  <button
                     type="button"
                     className="btn btn-primary" onClick={(e) => {
                     setUseTestData(false);
                     backToForm({
                        step: currentStep,
                     })
                  }}
                  >
                     <i className="fa fa-pencil"></i> Edit again
                  </button>
                  
                  <Link
                     href={DASH_LINK_CAR_INSURANCE_QUOTE_LIST}
                     className="btn btn-primary">
                     <i className="fa fa-list"></i> Applications List
                  </Link>
               </div>
            )}
         
         
         </div>
      );
   }
   
   const onSubmit = async () => {
      
      if (!isEditableMode(mode)) {
         return;
      }
      
      let endPointUrl = BACKEND_CAR_INSURANCE_QUOTE_NEW;
      
      if (isEditMode(mode) && initialFormData.id) {
         endPointUrl = BACKEND_CAR_INSURANCE_QUOTE_UPDATE + (initialFormData.id || '');
      }
      
      await insuranceFormOnSubmit({
         formData,
         setFormData,
         executeRecaptcha,
         setIsSubmissionProcessing,
         setSubmissionErrorMsg,
         setServerValidationErrors,
         setSubmissionProcessingMsg,
         setSubmissionSuccessMsg,
         endPointUrl: endPointUrl,
         resetForm: isNewMode(mode),
      })
      
   };
   
   /**
    * This method allows navigating to any step of the form.
    *
    * @param step
    */
   
   function backToForm(
      {
         step = FIRST_STEP
      }
   ) {
      setIsSubmissionProcessing(false);
      setSubmissionErrorMsg('');
      setSubmissionSuccessMsg('');
      setServerValidationErrors([]);
      setCurrentStep(step);
      
      checkAndAddCssClassToRefContainerChildren({
         refContainer: cifContainerRef,
         selector: '.step-container',
         className: 'fade-in',
      });
   }
   
   const DetailsModeContent = () => {
      return (
         <div
            ref={formRef}
            className="insurance-form details-mode"
         >
            
            <div className={`step-container`} ref={stepContainerRef}>
               
               <SubmissionDetailsHeadingBlock
                  formData={formData}
                  editUrl={EDIT_URL + formData.id}
                  listUrl={LIST_URL}
               />
               
               <IF_Step0
                  data={formData}
                  handlers={eventHandlers}
                  setCurrentStep={setCurrentStep}
                  currentStep={currentStep}
               />
               
               <IF_Step1
                  data={formData}
                  handlers={eventHandlers}
                  setCurrentStep={setCurrentStep}
               />
               
               <IF_Step2
                  data={formData}
                  handlers={eventHandlers}
                  setCurrentStep={setCurrentStep}
               />
               
               <IF_Step3
                  data={formData}
                  handlers={eventHandlers}
                  setCurrentStep={setCurrentStep}
               />
               
               <IF_Step4
                  data={formData}
                  setFormData={setFormData}
                  handlers={eventHandlers}
                  setCurrentStep={setCurrentStep}
                  handlePolicyHolderAccidentPropChange={handlePolicyHolderAccidentPropChange}
                  handlePolicyHolderConvictionPropChange={handlePolicyHolderConvictionPropChange}
                  addPolicyHolderAccident={addPolicyHolderAccident}
                  addPolicyHolderConviction={addPolicyHolderConviction}
                  removePolicyHolderAccident={removePolicyHolderAccident}
                  removePolicyHolderConviction={removePolicyHolderConviction}
               />
               
               <IF_Step5
                  data={formData}
                  handlers={eventHandlers}
                  setCurrentStep={setCurrentStep}
                  addAdditionalDriver={addAdditionalDriver}
                  handleAdditionalDiverPropChange={handleAdditionalDiverPropChange}
                  removeAdditionalDiverByIndex={removeAdditionalDiverByIndex}
                  handleAdditionalDriverAccidentPropChange={handleAdditionalDriverAccidentPropChange}
                  addAdditionalDriverAccident={addAdditionalDriverAccident}
                  removeAdditionalDriverAccident={removeAdditionalDriverAccident}
                  onSubmit={onSubmit}
                  formRef={formRef}
                  setFormData={setFormData}
                  
                  handleAdditionalDriverConvictionPropChange={handleAdditionalDriverConvictionPropChange}
                  addAdditionalDriverConviction={addAdditionalDriverConviction}
                  removeAdditionalDriverConviction={removeAdditionalDriverConviction}
               />
            </div>
         
         </div>
      );
   }
   
   return (
      <>
         
         
         <div className="cif-container" ref={cifContainerRef}>
            
            {isSubmissionProcessing && (
               <FormSubmissionProcessingLoader
                  text={isNewMode(mode) ?
                     'Submitting new application...' :
                     'Updating application...'}
               />
            )}
            
            {!isSubmissionProcessing && serverValidationErrors.length !== 0 && (
               <ServerValidationMessageList />
            )}
            
            {!isSubmissionProcessing && submissionErrorMsg && (
               
               <SubmissionErrorMessage />
            
            )}
            
            {!isSubmissionProcessing && submissionSuccessMsg && (
               
               <SubmissionSuccessMessage />
            
            )}
            
            
            {isEditableMode(mode) && !isSubmissionProcessing && submissionErrorMsg === '' && submissionSuccessMsg === '' && serverValidationErrors.length === 0 && (
               
               
               <form
                  ref={formRef}
                  className="insurance-form"
                  onSubmit={(e) => {
                     e.preventDefault();
                     
                     onSubmit(e);
                  }}>
                  
                  {isNewMode(mode) && (
                     <UseTestData
                        useTestData={useTestData}
                        setUseTestData={setUseTestData}
                        setFormData={setFormData}
                        formData={formData}
                        testData={TestData}
                     />
                  )}

                  <QuoteFormSeoIntro startYourQuoteHeading="Start Your Car Insurance Quote" />
                  
                  
                  {currentStep === 0 && (
                     <div className={`step-container`} ref={stepContainerRef}>
                        <IF_Step0
                           data={formData}
                           handlers={eventHandlers}
                           setCurrentStep={setCurrentStep}
                           currentStep={currentStep}
                        />
                     </div>
                  )}
                  
                  {currentStep === 1 && (
                     <div className={`step-container`} ref={stepContainerRef}>
                        <IF_Step1
                           data={formData}
                           handlers={eventHandlers}
                           setCurrentStep={setCurrentStep}
                        />
                     </div>
                  )}
                  
                  {currentStep === 2 && (
                     <div className={`step-container`} ref={stepContainerRef}>
                        <IF_Step2
                           data={formData}
                           handlers={eventHandlers}
                           setCurrentStep={setCurrentStep}
                        />
                     </div>
                  )}
                  
                  {currentStep === 3 && (
                     <div className={`step-container`} ref={stepContainerRef}>
                        <IF_Step3
                           data={formData}
                           handlers={eventHandlers}
                           setCurrentStep={setCurrentStep}
                        />
                     </div>
                  )}
                  
                  {currentStep === 4 && (
                     <div className={`step-container`} ref={stepContainerRef}>
                        <IF_Step4
                           data={formData}
                           setFormData={setFormData}
                           handlers={eventHandlers}
                           setCurrentStep={setCurrentStep}
                           handlePolicyHolderAccidentPropChange={handlePolicyHolderAccidentPropChange}
                           handlePolicyHolderConvictionPropChange={handlePolicyHolderConvictionPropChange}
                           addPolicyHolderAccident={addPolicyHolderAccident}
                           addPolicyHolderConviction={addPolicyHolderConviction}
                           removePolicyHolderAccident={removePolicyHolderAccident}
                           removePolicyHolderConviction={removePolicyHolderConviction}
                        />
                     </div>
                  )}
                  
                  {currentStep === 5 && (
                     <div className={`step-container`} ref={stepContainerRef}>
                        <IF_Step5
                           data={formData}
                           handlers={eventHandlers}
                           setCurrentStep={setCurrentStep}
                           addAdditionalDriver={addAdditionalDriver}
                           handleAdditionalDiverPropChange={handleAdditionalDiverPropChange}
                           removeAdditionalDiverByIndex={removeAdditionalDiverByIndex}
                           handleAdditionalDriverAccidentPropChange={handleAdditionalDriverAccidentPropChange}
                           addAdditionalDriverAccident={addAdditionalDriverAccident}
                           removeAdditionalDriverAccident={removeAdditionalDriverAccident}
                           onSubmit={onSubmit}
                           formRef={formRef}
                           setFormData={setFormData}
                           
                           handleAdditionalDriverConvictionPropChange={handleAdditionalDriverConvictionPropChange}
                           addAdditionalDriverConviction={addAdditionalDriverConviction}
                           removeAdditionalDriverConviction={removeAdditionalDriverConviction}
                        />
                     </div>
                  )}
               
               </form>
            
            
            )}
            
            {isDetailsMode(mode) && !isSubmissionProcessing && submissionErrorMsg === '' && submissionSuccessMsg === '' && serverValidationErrors.length === 0 && (
               
               
               <DetailsModeContent />
            
            
            )}
         
         </div>
         
         {isEditMode(mode) && (
            <QuickStepList
               stepList={STEP_LIST}
               currentStep={currentStep}
               backToForm={backToForm}
               onSubmit={onSubmit}
            />
         )}
      </>
   
   );
   
}